import { SearchOutlined } from "@ant-design/icons";
import { Input } from "antd/es";
import _ from "lodash";
import React, { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import { KDatePicker } from "../../date/date";
import { ValueType } from "../base-table";
import TriStateSwitch from "../tri-state-switch";

interface TableHeaderCellProps {
  style?: any;
  columnStyle?: any;
  textDirection?: string;
  columnSearchText?: string;
  setColumnSearchText?: (key, value) => void;
  title?: string;
  secondTitle?: string;
  children?: any;
  searchable?: boolean;
  type?: ValueType;
  dataValue?: string;
  resizeListener?: (e: MouseEvent) => void;
  setResizingColumnIndex?: (index: number, startX: number) => void;
  resizableColumns?: boolean;
  isSearchHidden?: boolean;
  className?: string;
  isFilteringHidden?: boolean;
  isSortingHidden?: boolean;
  tableFiltering?: boolean;
  tableSorting?: boolean;
  index?: number;
  onHeaderSearchBlur?: () => void;
  hasGrouping?: boolean;
  scope?: string;
  isGroupCell?: boolean;
  shouldSearchOnChange?: boolean;
  searchDelay?: number;
  titleBgColor?: string;
  placeholder?: string;
  left?: number;
  right?: number;
  filteredValue?: any[];
  groupBackgroundColor?: string;
  groupFontColor?: string;
  groupFontSize?: number;
  groupFontWeight?: string;
  headerStyle?: any;
  isFirstColumn?: boolean;
  isLastColumn?: boolean;
  dateSearchFormat?: string;
  tableBgColor?: string;
  getContrastTextColor?: (bgColor: string) => string;
}

interface TableHeaderCellState {
  searchValue: string | boolean | null;
}

declare let window: any;

export class TableHeaderCell extends PureComponent<TableHeaderCellProps, TableHeaderCellState> {
  debouncedSetSearchText: (value: string) => void;

  constructor(props: TableHeaderCellProps) {
    super(props);
    this.state = {
      searchValue: ""
    };
    const delay = props.searchDelay || 200;
    this.debouncedSetSearchText = _.debounce(this.setSearchText.bind(this), delay);
  }

  getStyle = (): any => {
    let style: any = {};
    if (this.props.style) {
      style = _.clone(this.props.style);
    }

    if (this.props.isGroupCell) {
      const borderColor = style.borderColor || "rgb(218, 218, 218)";
      style = {
        ...style,
        borderTop: `1px solid ${borderColor} !important`,
        borderBottom: `1px solid ${borderColor} !important`,
        borderRight: `1px solid ${borderColor} !important`,
        borderLeft: `1px solid ${borderColor} !important`,
        textAlign: "center"
      };

      if (this.props.groupBackgroundColor) {
        style.background = this.props.groupBackgroundColor;
      }

      if (this.props.groupFontColor) {
        style.color = this.props.groupFontColor;
      }

      if (this.props.groupFontSize) {
        style.fontSize = `${this.props.groupFontSize}px`;
      }

      if (this.props.groupFontWeight) {
        const fontWeightMap: { [key: string]: string | number } = {
          Thin: 100,
          "Extra Light": 200,
          Light: 300,
          Regular: 400,
          Medium: 500,
          "Semi Bold": 600,
          Bold: 700,
          "Extra Bold": 800,
          Black: 900
        };
        style.fontWeight = fontWeightMap[this.props.groupFontWeight] || this.props.groupFontWeight;
      }
    } else if (
      !this.props.hasGrouping &&
      !style.border &&
      !style.borderColor &&
      !style.borderTop &&
      !style.borderBottom &&
      !style.borderRight &&
      !style.borderLeft &&
      !style.borderWidth
    ) {
      style.borderWidth = 0;
    }

    if (this.props.titleBgColor) {
      style.backgroundColor = this.props.titleBgColor;
    } else if (!style.backgroundColor && !style.background) {
      style.backgroundColor = "transparent";
    }

    if (this.props.columnStyle && this.props.columnStyle.textAlign) {
      style.textAlign = this.props.columnStyle.textAlign;
    }
    if (this.props.textDirection && this.props.textDirection !== "Default") {
      if (style.textAlign === "-webkit-left") {
        style.verticalAlign = this.props.textDirection === "Rotate Up" ? "bottom" : "top";
      } else if (style.textAlign === "-webkit-right") {
        style.verticalAlign = this.props.textDirection === "Rotate Up" ? "top" : "bottom";
      } else {
        style.verticalAlign = "middle";
      }
      style.textAlign = "-webkit-center";
    }
    if (this.props.columnStyle && this.props.columnStyle.padding) {
      style.paddingLeft = this.props.columnStyle.padding;
      style.paddingRight = this.props.columnStyle.padding;
    }
    if (this.props.columnStyle && this.props.columnStyle.paddingLeft) {
      style.paddingLeft = this.props.columnStyle.paddingLeft;
    }
    if (this.props.columnStyle && this.props.columnStyle.paddingRight) {
      style.paddingRight = this.props.columnStyle.paddingRight;
    }

    if (this.props.headerStyle) {
      if (this.props.isFirstColumn && this.props.headerStyle.paddingLeft) {
        style["--padding-left"] = `${this.props.headerStyle.paddingLeft}px`;
      }
      if (this.props.isLastColumn && this.props.headerStyle.paddingRight) {
        style["--padding-right"] = `${this.props.headerStyle.paddingRight}px`;
      }
    }

    if (style?.display) {
      delete style.display;
    }
    if (this.props.left && !window.kuika.isDesignTime) {
      style.left = this.props.left;
    }
    if (this.props.right && !window.kuika.isDesignTime) {
      style.right = this.props.right;
    }
    return style;
  };

  componentDidMount(): void {
    const { columnSearchText, dataValue, type } = this.props;
    if (columnSearchText && columnSearchText[dataValue] && dataValue) {
      this.setState({ searchValue: columnSearchText[dataValue] });
      if (window.kuika.columnSearchChanged === dataValue) {
        const tableHeader = ReactDOM.findDOMNode(this) as Element;
        const input: any = tableHeader.querySelector("input");
        if (input) {
          setTimeout(() => {
            input.focus();
            window.kuika.columnSearchChanged = null;
          }, 0);
        }
      }
    } else if (
      dataValue &&
      columnSearchText &&
      type === ValueType.Boolean &&
      (columnSearchText[dataValue] === null ||
        columnSearchText[dataValue] === false ||
        columnSearchText[dataValue] === true ||
        columnSearchText[dataValue] === "false" ||
        columnSearchText[dataValue] === "true")
    ) {
      this.setState({ searchValue: columnSearchText[dataValue] });
    }

    const tableHeader = ReactDOM.findDOMNode(this) as Element;
    const { style, filteredValue } = this.props;
    if (!tableHeader) return;
    const antTableFilterTrigger = tableHeader.querySelector(".ant-table-filter-trigger");
    const antTableColumnSorter = tableHeader.querySelector(".ant-table-column-sorter");

    const hasActiveFilter = filteredValue && filteredValue.length > 0;

    if (style.color) {
      if (antTableFilterTrigger) {
        antTableFilterTrigger.setAttribute("style", `color: ${style.color}`);
      }
      if (antTableColumnSorter) {
        antTableColumnSorter.setAttribute("style", `color: ${style.color}`);
      }
    }

    if (hasActiveFilter && antTableFilterTrigger) {
      antTableFilterTrigger.setAttribute("style", `color: #1677ff !important`);
    }
  }

  componentDidUpdate(prevProps: TableHeaderCellProps): void {
    const { style, filteredValue } = this.props;
    const hasFilterStateChanged = (prevProps.filteredValue?.length || 0) !== (filteredValue?.length || 0);

    if (hasFilterStateChanged) {
      const tableHeader = ReactDOM.findDOMNode(this) as Element;
      if (!tableHeader) return;

      const antTableFilterTrigger = tableHeader.querySelector(".ant-table-filter-trigger");
      const antTableColumnSorter = tableHeader.querySelector(".ant-table-column-sorter");

      const hasActiveFilter = filteredValue && filteredValue.length > 0;

      if (style.color) {
        if (antTableFilterTrigger) {
          antTableFilterTrigger.setAttribute("style", `color: ${style.color}`);
        }
        if (antTableColumnSorter) {
          antTableColumnSorter.setAttribute("style", `color: ${style.color}`);
        }
      }

      if (hasActiveFilter && antTableFilterTrigger) {
        antTableFilterTrigger.setAttribute("style", `color: #1677ff !important`);
      }
    }
  }

  setSearchText(value: string) {
    const { setColumnSearchText, dataValue } = this.props;
    if (setColumnSearchText && dataValue) {
      setColumnSearchText(dataValue, value);
    }
    window.kuika.columnSearchChanged = dataValue;
  }

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ searchValue: value }, () => {
      if (this.props.type === ValueType.Date) return;
      if (
        (value?.length > 0 && this.props.shouldSearchOnChange) ||
        value === "" ||
        value === null ||
        value === undefined
      ) {
        this.debouncedSetSearchText(value);
      }
    });
  };

  handleSearchBlur = () => {
    const { setColumnSearchText, dataValue } = this.props;
    if (setColumnSearchText && dataValue) {
      setColumnSearchText(dataValue, this.state.searchValue);
    }
    if (this.props.onHeaderSearchBlur) this.props.onHeaderSearchBlur();
  };

  renderInput = () => {
    const {
      type,
      columnSearchText,
      dataValue,
      placeholder,
      dateSearchFormat,
      style,
      titleBgColor,
      tableBgColor,
      getContrastTextColor
    } = this.props;
    const language = localStorage.getItem("ml");
    const { searchValue } = this.state;

    const isDesignTime = window.kuika.isDesignTime;

    const headerBgColor = titleBgColor;
    const bgColor = headerBgColor || tableBgColor;
    const textColor = getContrastTextColor ? getContrastTextColor(bgColor) : "#000000";

    let inputType = "text";
    if (type === ValueType.Number) inputType = "number";
    else if (type === ValueType.Date) {
      if (
        dateSearchFormat &&
        (dateSearchFormat.includes("T") ||
          dateSearchFormat.includes("HH:mm:ss") ||
          dateSearchFormat.includes("HH:mm") ||
          dateSearchFormat.includes("mm:ss"))
      )
        inputType = "datetime";
      else if (!dateSearchFormat || dateSearchFormat === "YYYY-MM-DD") inputType = "date";
      else inputType = "text";
    } else inputType = "text";
    if (type === ValueType.Boolean) {
      return (
        <TriStateSwitch
          value={columnSearchText[dataValue] === true ? true : columnSearchText[dataValue] === false ? false : null}
          onChange={(value: boolean | null) => {
            this.setState({ searchValue: value }, () => {
              this.handleSearchBlur();
              if (this.props.onHeaderSearchBlur) this.props.onHeaderSearchBlur();
            });
          }}
          language={language}
          source={"TableHeaderCell"}
        />
      );
    }
    if (type === ValueType.Date) {
      console.log("dateSearchFormat", dateSearchFormat);
      let displayType =
        dateSearchFormat &&
        (dateSearchFormat.includes("T") ||
          dateSearchFormat.includes("HH:mm:ss") ||
          dateSearchFormat.includes("HH:mm") ||
          dateSearchFormat.includes("mm:ss"))
          ? "datetime"
          : "date";
      const hasTimeInValue =
        typeof searchValue === "string" && (searchValue.includes(":") || searchValue.includes("T"));
      if (hasTimeInValue) {
        displayType = "datetime";
      }
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <KDatePicker
            format={dateSearchFormat || "DD.MM.YYYY"}
            displayType={displayType as any}
            placeholder={placeholder || (language === "tr_TR" ? "Ara..." : "Search...")}
            value={typeof searchValue === "string" ? searchValue : ""}
            isTableSearch
            onChange={(val) => {
              this.setState({ searchValue: val || "" }, () => {
                if (val === null || val === undefined || val === "") {
                  this.handleSearchBlur();
                  if (this.props.onHeaderSearchBlur) this.props.onHeaderSearchBlur();
                  return;
                }
                this.handleSearchBlur();
                if (this.props.onHeaderSearchBlur) this.props.onHeaderSearchBlur();
              });
            }}
            onPressEnter={(e) => {
              e.stopPropagation();
              this.handleSearchBlur();
            }}
            disabled={isDesignTime}
            returnDisplayValue
            style={{ color: textColor }}
          />
        </div>
      );
    }
    return (
      <Input
        type={inputType}
        placeholder={placeholder || (language === "tr_TR" ? "Ara..." : "Search...")}
        value={typeof searchValue === "string" ? searchValue : String(searchValue || "")}
        onClick={(e) => e.stopPropagation()}
        onChange={this.handleSearchChange}
        onBlur={this.handleSearchBlur}
        onPressEnter={(e) => {
          e.stopPropagation();
          this.handleSearchBlur();
        }}
        style={{ color: textColor }}
        suffix={
          <SearchOutlined
            style={{ color: textColor }}
            onClick={(e) => {
              e.stopPropagation();
              this.handleSearchBlur();
            }}
          />
        }
        readOnly={isDesignTime}
      />
    );
  };

  private getClassNames = (): string => {
    const {
      className = "",
      isSearchHidden,
      isFilteringHidden,
      isSortingHidden,
      tableSorting,
      tableFiltering,
      type,
      secondTitle,
      style
    } = this.props;

    const classes = new Set([className]);

    if (secondTitle && secondTitle !== "") {
      classes.add("has-secondary-title");
    }

    if (isSearchHidden) {
      if (style?.alignItems !== "flex-start") {
        classes.add("hidden-search");
      }

      const alignment = style?.alignItems;

      if (alignment === "flex-end") {
        classes.add("hidden-search-end");

        if (!tableSorting) {
          classes.add("hidden-search-end--without-sorter");
        }
        if (!tableFiltering) {
          classes.add("hidden-search-end--without-filtering");
        }
        if (!tableFiltering && tableSorting) {
          classes.add("hidden-search-end--without-filtering-and-sorter");
        }
      }

      if (alignment === "flex-start") {
        if (!tableSorting) {
          classes.add("hidden-search-start--without-sorter");
        }
        if (!tableFiltering) {
          classes.add("hidden-search-start--without-filtering");
        }
        if (!tableFiltering && tableSorting) {
          classes.add("hidden-search-start--without-filtering-and-sorter");
        }
      }

      if (!alignment || alignment === "center") {
        if (!tableFiltering && tableSorting) {
          classes.add("hidden-search-center--without-filtering-and-sorter");
        }
      }
    }

    if (!isSearchHidden && type === ValueType.Boolean && !tableSorting && this.props.searchable) {
      classes.add("hidden-sorting-without-filtering--boolean");
    }

    if (isSortingHidden) {
      if (!isFilteringHidden && !isSearchHidden) {
        classes.add("hidden-sorting");
      }
      if (isSearchHidden && !isFilteringHidden) {
        classes.add("hidden-filtering");
      }
    }

    if (isFilteringHidden && !isSortingHidden && !isSearchHidden && tableFiltering) {
      classes.add("hidden-filtering");
    }
    if (!this.props.searchable) {
      classes.add("hidden-search");
    }

    if (this.props.headerStyle) {
      if (
        this.props.isFirstColumn &&
        (!this.props.headerStyle.paddingLeft || this.props.headerStyle.paddingLeft === 8)
      ) {
        classes.add("no-padding-left");
      }
      if (
        this.props.isLastColumn &&
        (!this.props.headerStyle.paddingRight || this.props.headerStyle.paddingRight === 8)
      ) {
        classes.add("no-padding-right");
      }
    }

    return Array.from(classes).filter(Boolean).join(" ");
  };

  render(): ReactNode {
    const hasSearchable = this.props.dataValue || typeof this.props.children?.[1] === "object";
    const screenWidth = window.innerWidth;
    let title: ReactNode = this.props.title;
    if (this.props.secondTitle && this.props.secondTitle !== "") {
      title = (
        <div className="column-title-container">
          <div className="column-title">{this.props.children}</div>
          <div className="column-secondary-title">{this.props.secondTitle}</div>
        </div>
      );
    }

    return (
      <th {...this.props} className={this.getClassNames()} style={this.getStyle()} role="columnheader">
        {this.props.resizableColumns && !window.kuika.isDesignTime && screenWidth > 1024 && (
          <div
            className="resizeHandle"
            onMouseDown={(e) => {
              this.props.setResizingColumnIndex(this.props.index, e.clientX);
              window.addEventListener("mousemove", this.props.resizeListener, { passive: true });
            }}
          />
        )}
        {this.props.secondTitle && this.props.secondTitle !== "" ? title : this.props.children && this.props.children}
        {this.props.title &&
          this.props.title !== "" &&
          this.props.searchable &&
          hasSearchable &&
          !this.props.isSearchHidden &&
          this.renderInput()}
      </th>
    );
  }
}
